import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { Modifier } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import React from "react";

import { Table, TableBody, TableCell, TableHead, TableHeader } from "@/components/ui/v2/table";
import { cn } from "@/lib/utils/cn";

const restrictToVerticalWithinParent: Modifier = ({
  transform,
  draggingNodeRect,
  containerNodeRect,
}) => {
  if (!draggingNodeRect || !containerNodeRect) {
    return { ...transform, x: 0 };
  }

  const minY = containerNodeRect.top - draggingNodeRect.top;
  const maxY = containerNodeRect.top + containerNodeRect.height - draggingNodeRect.top - draggingNodeRect.height;

  return {
    ...transform,
    x: 0,
    y: Math.min(Math.max(transform.y, minY), maxY),
  };
};

export interface SortableColumn<T> {
  header: string;
  accessor: keyof T;
  width?: string;
  Cell?: (row: T) => React.ReactNode;
}

interface SortableTableRowProps<T> {
  id: string;
  row: T;
  columns: SortableColumn<T>[];
}

function SortableTableRow<T>({ id, row, columns }: SortableTableRowProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <tr
      ref={setNodeRef}
      data-slot="table-row"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "hover-surface-primary border-b transition-colors",
        isDragging && "opacity-50 bg-muted"
      )}
    >
      <TableCell className="w-10">
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      {columns.map((col) => (
        <TableCell key={String(col.accessor)} style={{ width: col.width }}>
          {col.Cell ? col.Cell(row) : String(row[col.accessor] ?? "")}
        </TableCell>
      ))}
    </tr>
  );
}

interface SortableTableProps<T> {
  columns: SortableColumn<T>[];
  data: T[];
  itemKey: keyof T;
  dragColumnLabel?: string;
  onReorder: (newData: T[]) => void;
}

function SortableTable<T>({
  columns,
  data,
  itemKey,
  dragColumnLabel = "",
  onReorder,
}: SortableTableProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = data.findIndex(
        (item) => String(item[itemKey]) === active.id
      );
      const newIndex = data.findIndex(
        (item) => String(item[itemKey]) === over.id
      );
      onReorder(arrayMove(data, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalWithinParent]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={data.map((item) => String(item[itemKey]))}
        strategy={verticalListSortingStrategy}
      >
        <Table>
          <TableHeader>
            <tr className="border-b">
              <TableHead className="w-10">{dragColumnLabel}</TableHead>
              {columns.map((col) => (
                <TableHead key={String(col.accessor)} style={{ width: col.width }}>
                  {col.header}
                </TableHead>
              ))}
            </tr>
          </TableHeader>
          <TableBody>
            {data.map((row) => (
              <SortableTableRow
                key={String(row[itemKey])}
                id={String(row[itemKey])}
                row={row}
                columns={columns}
              />
            ))}
          </TableBody>
        </Table>
      </SortableContext>
    </DndContext>
  );
}

export { SortableTable };
